import type { DefaultPricingProperty } from '../schemas/default-pricing';
import { CATEGORY_MARKUP_PROPERTY_BY_CATEGORY } from '../schemas/default-pricing';
import { resolvePricingLegacyCategoryKey } from './content-categories';
import { roundPercentValue } from './engine';

// The organisation's default markups (settings → Pricing Configuration →
// Default Pricing) as the pricing reads them, shared so the itinerary write
// paths and the product editor's Price preview resolve one percent the same
// way (XT-951 #38). Loading the rows stays with each caller: the API reads
// the table, the editor reads `defaultPricing.findCategoryMarkups`.

export type PricingSettingCountryValue = { country: string; value: number };

/** One active Default Pricing row: its default and per-country overrides. */
export type PricingSetting = {
  default: number;
  countryValues: PricingSettingCountryValue[];
};

export type CategoryMarkupSettings = ReadonlyMap<
  DefaultPricingProperty,
  PricingSetting
>;

// Country override beats the row default. Case-insensitive country match,
// mirroring legacy `getCountrySpecificOrDefaultValue`. Returns null when the
// property has no active row at all.
export const resolveDefaultPricingValue = (
  setting: PricingSetting | undefined,
  country: string | null | undefined
): number | null => {
  if (!setting) return null;
  if (country) {
    const match = setting.countryValues.find(
      (cv) => cv.country.toLowerCase() === country.toLowerCase()
    );
    if (match) return match.value;
  }
  return setting.default;
};

// The markup percent configured for a content category, or null when the org
// configured none. `contentCategory` is normalised through the pricing
// module's own resolver, so both the label form ('Accommodation') and the
// legacy key form ('ACCOMMODATION') resolve to the same setting.
//
// A country override beats the row default via `resolveDefaultPricingValue`
// (legacy `getCountrySpecificOrDefaultValue`) — a case-insensitive EXACT string
// match against the product's `country`.
//
// ⚠️ Known limitation, inherited from the subitem path: the two sides speak
// different country vocabularies. `products.product.country` holds English
// names ('Brazil'), while the Default Pricing form's `CountrySelect` writes
// ISO alpha-2 codes ('BR') and `countryValueSchema` caps the field at 3 chars
// — so a name cannot even be stored through the UI, and an ISO override
// therefore never matches. The category default resolves from the row
// default. See `apps/auth/prd/default-category-markup.md` → Remaining risks.
export const resolveCategoryMarkupPercent = ({
  settings,
  contentCategory,
  country,
}: {
  settings: CategoryMarkupSettings;
  contentCategory: string | null | undefined;
  country: string | null | undefined;
}): number | null => {
  if (settings.size === 0) return null;

  const categoryKey = resolvePricingLegacyCategoryKey(contentCategory);
  if (!categoryKey) return null;

  const property = CATEGORY_MARKUP_PROPERTY_BY_CATEGORY[categoryKey];
  const percent = resolveDefaultPricingValue(settings.get(property), country);
  if (percent === null || percent === 0) return null;

  return roundPercentValue(percent);
};
